/**
 * Intervention enums + helpers (mirror of backend).
 * Status order is the logical evolution of an intervention.
 */

export type InterventionType =
  | "Helpdesk"
  | "Onderhoud"
  | "Keuring"
  | "Herstelling"
  | "Controle"
  | "Overige";

export type InterventionStatus =
  | "In te plannen"
  | "Bevestiging te ontvangen van leverancier"
  | "Gepland"
  | "Feedback ontvangen"
  | "Huurder niet thuis"
  | "Leverancier niet geweest"
  | "Opgelost";

export const INTERVENTION_TYPES: { value: InterventionType; label: string }[] = [
  { value: "Helpdesk", label: "Helpdesk" },
  { value: "Onderhoud", label: "Onderhoud" },
  { value: "Keuring", label: "Keuring" },
  { value: "Herstelling", label: "Herstelling" },
  { value: "Controle", label: "Controle" },
  { value: "Overige", label: "Overige" },
];

// Logical order — keep in sync with backend enum order.
export const INTERVENTION_STATUSES: {
  value: InterventionStatus;
  label: string;
}[] = [
  { value: "In te plannen", label: "In te plannen" },
  {
    value: "Bevestiging te ontvangen van leverancier",
    label: "Bevestiging te ontvangen van leverancier",
  },
  { value: "Gepland", label: "Gepland" },
  { value: "Feedback ontvangen", label: "Feedback ontvangen" },
  { value: "Huurder niet thuis", label: "Huurder niet thuis" },
  { value: "Leverancier niet geweest", label: "Leverancier niet geweest" },
  { value: "Opgelost", label: "Opgelost" },
];

export const INTERVENTION_CLOSURE_STATUSES: InterventionStatus[] = [
  "Opgelost",
  "Huurder niet thuis",
  "Leverancier niet geweest",
];

export const isInterventionClosed = (status: InterventionStatus): boolean =>
  INTERVENTION_CLOSURE_STATUSES.includes(status);

export const isInterventionOverdue = (
  status: InterventionStatus,
  plannedDate: string | null | undefined,
): boolean => {
  if (!plannedDate) return false;
  if (isInterventionClosed(status)) return false;
  return new Date(plannedDate).getTime() < Date.now();
};

/**
 * Tailwind classes for the status chip — picks color based on phase.
 */
export const getInterventionStatusChipClass = (
  status: InterventionStatus,
): string => {
  switch (status) {
    case "In te plannen":
      return "border-neutral-300 bg-neutral-100 text-neutral-700";
    case "Bevestiging te ontvangen van leverancier":
      return "border-amber-300 bg-amber-50 text-amber-800";
    case "Gepland":
      return "border-blue-300 bg-blue-50 text-blue-700";
    case "Feedback ontvangen":
      return "border-indigo-300 bg-indigo-50 text-indigo-700";
    case "Huurder niet thuis":
    case "Leverancier niet geweest":
      return "border-red-300 bg-red-50 text-red-700";
    case "Opgelost":
      return "border-emerald-300 bg-emerald-50 text-emerald-700";
    default:
      return "border-neutral-300 bg-neutral-50 text-neutral-700";
  }
};

export interface InterventionDTO {
  id: number;
  ticketId: number;
  type: InterventionType;
  status: InterventionStatus;
  plannedDate: string | null;
  durationMinutes: number;
  description: string | null;
  notesForAttendees: string | null;
  tenantAccessRequired: boolean;
  sendInvite: boolean;
  additionalInviteeContactIds: number[] | null;
  supplierId: number | null;
  supplier: { id: number; name: string } | null;
  calendarEventId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}
